import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'

import { mapShipmentToOrderSummary } from '../model/map-order-summary'
import type { CarrierRequestsHandlers } from '../model/types'
import { useCarrierRequests } from '../model/use-carrier-requests'
import { useTransitionRequest } from '../model/use-transition-request'
import { CarrierRequestsDialog } from './carrier-requests-dialog'

interface OrderCarrierRequestsDialogProps {
  shipmentId: string | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

function toFiniteNumber(
  raw: number | string | null | undefined,
): number | undefined {
  if (raw == null || raw === '') return undefined
  const num = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(num) ? num : undefined
}

export function OrderCarrierRequestsDialog({
  shipmentId,
  open,
  onOpenChange,
}: OrderCarrierRequestsDialogProps) {
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(
    shipmentId ?? '',
    { query: { enabled: open && Boolean(shipmentId) } },
  )
  const shipment = shipmentQuery.data?.data
  const carrierPay = toFiniteNumber(shipment?.carrier_pay)
  // Baseline for the "over COD" badge: cod_amount when set, otherwise carrier pay.
  const dueOnDelivery = toFiniteNumber(shipment?.cod_amount) ?? carrierPay

  const { requests, totalDispatchedByYou, isLoading } = useCarrierRequests({
    shipmentId,
    enabled: open,
    dueOnDelivery,
    carrierPay,
  })

  const transition = useTransitionRequest({
    shipmentId,
    onSuccess: () => onOpenChange(false),
  })
  const handlers: CarrierRequestsHandlers = {
    onAcceptBid: transition.accept,
    onAcceptRequest: transition.accept,
    onCounterOffer: transition.counterOffer,
    onDecline: transition.decline,
    onCancelOffer: transition.cancel,
    onCancelCounterOffer: transition.cancel,
    onSendOffer: transition.sendOffer,
  }

  const order = shipment ? mapShipmentToOrderSummary(shipment) : undefined
  return (
    <CarrierRequestsDialog
      open={open}
      onOpenChange={onOpenChange}
      order={order}
      requests={requests}
      totalDispatchedByYou={totalDispatchedByYou}
      isLoading={isLoading || shipmentQuery.isLoading}
      pending={transition.pending}
      handlers={handlers}
    />
  )
}
